// packages/auth/src/guards/PermissionGuard.tsx

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../useAuth';

interface PermissionGuardProps {
  resource: string;
  action: string;
  children: ReactNode;
  /** Controls behavior when access is denied.
   *  - 'redirect' (default): navigates to /dashboard with a console warning
   *  - 'inline': renders inline permission-denied message
   *  - ReactNode: renders custom fallback */
  fallback?: 'redirect' | 'inline' | ReactNode;
}

/**
 * Guard component that checks permission before rendering children.
 * Supports the 'manage' wildcard: if user has resource:manage, any action is allowed.
 */
export function PermissionGuard({
  resource,
  action,
  children,
  fallback = 'redirect',
}: PermissionGuardProps) {
  const { permissions, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const requiredPermission = `${resource}:${action}`;
  const hasAccess =
    permissions.includes(requiredPermission) || permissions.includes(`${resource}:manage`);

  const dispatchedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoading && !hasAccess && fallback === 'redirect') {
      console.warn(
        `[PermissionGuard] ${t('permissions.access_denied_toast')} (${requiredPermission})`,
      );
      // Dispatch event for toast notification (handled by apps/web). Once per denied
      // permission so StrictMode double-invoke / re-renders never stack duplicate toasts.
      if (dispatchedForRef.current !== requiredPermission) {
        dispatchedForRef.current = requiredPermission;
        try {
          window.dispatchEvent(
            new CustomEvent('alamo:permission-denied', {
              detail: {
                permission: requiredPermission,
                message: t('permissions.access_denied_toast'),
              },
            }),
          );
        } catch {
          // Ignore if dispatch fails (e.g., during SSR)
        }
      }
      navigate('/dashboard', { replace: true });
    }
  }, [isLoading, hasAccess, fallback, navigate, t, requiredPermission]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-alamo-orange-500" />
      </div>
    );
  }

  if (!hasAccess) {
    // Redirect mode: return null while useEffect triggers navigation
    if (fallback === 'redirect') {
      return null;
    }

    // Inline mode: render permission-denied message
    if (fallback === 'inline') {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
          <div className="mb-4 text-5xl" aria-hidden="true">
            &#128274;
          </div>
          <h2 className="mb-2 text-xl font-heading font-semibold text-slate-800">
            {t('permissions.denied_title')}
          </h2>
          <p className="max-w-sm text-slate-500">{t('permissions.denied_message')}</p>
        </div>
      );
    }

    // Custom fallback ReactNode
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
